import { useState } from 'react';
import { cn } from '@/shared/lib';
import type { DataTableProps, DataTableRow, ResolvedDataTableProps } from '@/shared/ui/data-table/types';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@/shared/ui/data-table/types';
import { dataTableStyles } from '@/shared/ui/data-table/styles';
import { usePresentation, useSavedViews, useTableQuery, useViewFilterReport } from '@/shared/ui/data-table/hooks';
import type { UseTableQueryState } from '@/shared/ui/data-table/hooks';
import { BulkBar } from './bulk-bar';
import { FilterBar } from './filter-bar';
import { TableBody } from './table-body';
import { TableFooter } from './table-footer';
import { TabsRow } from './tabs-row';

/**
 * One shared empty object, not `{}` inline: a fresh `filters` object on every
 * render is a fresh query identity on every render, and the fetch keys on it.
 */
const EMPTY_FILTERS: Record<string, string> = {};

/**
 * Applies the documented defaults.
 *
 * Split across two helpers because every defaulted field is a branch: one
 * function carrying all of them fails the complexity budget while doing nothing
 * harder than the button component does.
 */
function displayDefaults<TData extends DataTableRow>(props: DataTableProps<TData>) {
  return {
    presentation: props.presentation ?? 'table',
    caption: props.caption ?? 'Results',
    errorMessage: props.errorMessage ?? 'These results could not be loaded.',
    compact: props.compact ?? false,
  };
}

function behaviourDefaults<TData extends DataTableRow>(props: DataTableProps<TData>) {
  return {
    defaultPage: props.defaultPage ?? 1,
    defaultPageSize: props.defaultPageSize ?? 10,
    pageSizeOptions: props.pageSizeOptions ?? [...DEFAULT_PAGE_SIZE_OPTIONS],
    paginated: props.paginated ?? true,
    showFooter: props.showFooter ?? true,
    toolbar: props.toolbar ?? true,
    sortControls: props.sortControls ?? true,
    filters: props.filters ?? EMPTY_FILTERS,
    selectable: props.selectable ?? false,
  };
}

function withDefaults<TData extends DataTableRow>(props: DataTableProps<TData>): ResolvedDataTableProps<TData> {
  return { ...props, ...displayDefaults(props), ...behaviourDefaults(props) };
}

/**
 * Server-driven data table.
 *
 * The component holds the query — page, page size, sort, filters — and reports
 * it to `onFetch`. It never slices, sorts or filters the rows it is given,
 * because the rows it is given are one page and nothing else. `total`, not
 * `data.length`, is the size of the collection, and that distinction is what
 * keeps the footer and the page buttons honest.
 *
 * Layout, top to bottom:
 *   1. Tabs row — saved views, when supplied
 *   2. Filter bar — search, column-bound filters, view toggle
 *   3. Bulk bar — replaces the filter bar while rows are selected
 *   4. Table body — the rows themselves, or the empty / error / loading state
 *   5. Footer — pagination and per-page selector
 */
export function DataTable<TData extends DataTableRow = DataTableRow>(props: DataTableProps<TData>) {
  const resolved = withDefaults(props);

  // Page size state. Same pattern as presentation: controlled when `pageSize`
  // is supplied, else owned by the footer's per-page selector. The effective
  // size is threaded into the query, so choosing "50" changes the request and
  // not just the control. A new size usually invalidates the current page, so
  // the change handler also resets to page 1.
  const [internalPageSize, setInternalPageSize] = useState<number>(resolved.defaultPageSize);
  const pageSize = props.pageSize ?? internalPageSize;

  // Presentation state. Controlled by `presentation` prop when provided, else
  // owned internally so the view toggle can switch table <-> cards. On mobile
  // the default is cards and the toggle is hidden — see `usePresentation` for
  // why, and for how the default follows the breakpoint rather than being
  // fixed at the first render.
  //
  // Resolved BEFORE the query, because the query needs to know which
  // presentation is on screen: card view loads pages on scroll, so the
  // sentinel must be armed for it. `resolved.presentation` is the caller's
  // declared default and never changes when the toggle is clicked, so the
  // EFFECTIVE value is what the query is given.
  const { presentation, setPresentation } = usePresentation(props.presentation);

  const { activeViewId, applyView, activeViewFilters } = useSavedViews(resolved.views);

  // A saved view's filters are a claim about the collection, so they are merged into
  // the query the table sends — the tab narrows the data, not just its own label.
  // The view's filters win over the caller's, because the tab is the more recent
  // statement of intent: clicking "Draft" must show drafts even if the page also
  // declared a status filter.
  const viewFilters = activeViewFilters ?? EMPTY_FILTERS;
  const filters = { ...resolved.filters, ...viewFilters };
  const query = useTableQuery({ ...resolved, filters, presentation }, pageSize);

  // Switching a view returns to page one, and that is not a convenience — page 7 of a
  // newly filtered collection is not page 7 of the previous one, so leaving the page
  // alone would ask a question whose answer has nothing to do with what is on screen.
  // The reset lives here, not in `useSavedViews`, because only the caller can see the
  // filters change.
  const viewFilterKey = JSON.stringify(viewFilters);
  const [seenViewFilterKey, setSeenViewFilterKey] = useState(viewFilterKey);
  if (seenViewFilterKey !== viewFilterKey) {
    setSeenViewFilterKey(viewFilterKey);
    query.tableState.goToPage(1);
  }

  // A tab is a filter the user set, so it is reported upward exactly as a dropdown
  // filter is — see `useViewFilterReport` for why the report is an effect and not
  // part of the render-time reset above.
  useViewFilterReport(viewFilters, props.onColumnFiltersChange);

  // Going from 25 rows per page to 10 can leave the user on a page that no
  // longer exists, and the same is true of a filter change. Either way, the
  // truth is "page 1 of the new collection" and the table reflects it in the
  // same render rather than drawing a frame of the old page.
  const currentPageIndex = query.tableState.table.getState().pagination.pageIndex;
  if (currentPageIndex >= query.tableState.pageCount) {
    query.tableState.goToPage(1);
  }

  // An inert pager is noise: the footer renders only for a table that pages,
  // has more than one page, and has somewhere to report a page change to.
  //
  // Card view never shows it. A grid of cards is read by scrolling, and a
  // pager under a grid asks the user to page through records they are already
  // scanning — so card view loads the next page when the sentinel comes into
  // view instead, and the footer would be a second, contradictory way to move
  // through the same list.
  const showFooter =
    resolved.showFooter &&
    resolved.paginated &&
    (query.tableState.pageCount > 1 ||
      resolved.onPageChange !== undefined ||
      resolved.onPageSizeChange !== undefined);

  // Page size is owned above (controlled prop or the footer's selector) and
  // arrives here as the effective size; the query must be built from it.
  const onPageSizeChange = (next: number): void => {
    setInternalPageSize(next);
    query.tableState.goToPage(1);
  };

  return (
    <div className={cn(dataTableStyles.root, resolved.className)}>
      <DataTableSurface
        resolved={resolved}
        query={query}
        activeViewId={activeViewId}
        onViewChange={applyView}
        presentation={presentation}
        onPresentationChange={setPresentation}
        footer={
          showFooter && presentation === 'table' ? (
            <TableFooter
              page={query.tableState.table.getState().pagination.pageIndex + 1}
              pageCount={query.tableState.pageCount}
              pageSize={pageSize}
              total={resolved.total}
              rowCount={resolved.data.length}
              onPageChange={query.tableState.goToPage}
              pageSizeOptions={resolved.pageSizeOptions}
              onPageSizeChange={onPageSizeChange}
              compact={resolved.compact}
            />
          ) : null
        }
      />
    </div>
  );
}

type DataTableSurfaceProps<TData extends DataTableRow> = {
  resolved: ResolvedDataTableProps<TData>;
  query: UseTableQueryState<TData>;
  activeViewId: string | undefined;
  onViewChange: (id: string) => void;
  presentation: 'table' | 'cards';
  onPresentationChange: (next: 'table' | 'cards') => void;
  /** The footer slot. The orchestrator builds the footer and hands it down. */
  footer?: React.ReactNode;
};

/**
 * The inner card: tabs row, controls (filter or bulk bar), table body and
 * footer slot. The orchestrator owns state; this component owns layout.
 * The footer lives INSIDE the surface so the table and its pagination read
 * as one continuous panel; the footer's own top border separates it from
 * the last row.
 */
function DataTableSurface<TData extends DataTableRow>({
  resolved,
  query,
  activeViewId,
  onViewChange,
  presentation,
  onPresentationChange,
  footer,
}: DataTableSurfaceProps<TData>) {
  const { tableState, viewportRef, viewportStyle, sentinelRef } = query;
  const isCards = presentation === 'cards';

  /*
   * The chrome — saved views and the filter bar — is ONE sticky block in card
   * view, not two.
   *
   * Two separately-sticky siblings both pinned at `top-0` would slide over
   * each other: the filter bar would cover the tabs row as soon as the page
   * scrolled, and the tabs would be unreachable. Wrapping them in a single
   * sticky element makes them stick as a unit, so the tabs stay above the
   * filter bar and both stay above the cards.
   */
  const chrome = (
    <>
      {resolved.views && resolved.views.length > 0 ? (
        <TabsRow
          views={resolved.views}
          activeViewId={activeViewId}
          onViewChange={onViewChange}
          label="Saved views"
        />
      ) : null}
      <TableControls
        resolved={resolved}
        query={query}
        presentation={presentation}
        onPresentationChange={onPresentationChange}
      />
    </>
  );

  return (
    <div className={isCards ? dataTableStyles.surfaceBare : dataTableStyles.surface}>
      {isCards ? (
        <div className={dataTableStyles.stickyChrome}>
          <div className={dataTableStyles.stickyChromeFrame}>{chrome}</div>
        </div>
      ) : (
        chrome
      )}

      <TableBody
        presentation={presentation}
        resolved={resolved}
        table={tableState.table}
        viewportRef={viewportRef}
        viewportStyle={viewportStyle}
        showSentinel={isCards || !resolved.paginated}
        sentinelRef={sentinelRef}
      />

      {footer}
    </div>
  );
}

type TableControlsProps<TData extends DataTableRow> = {
  resolved: ResolvedDataTableProps<TData>;
  query: UseTableQueryState<TData>;
  presentation: 'table' | 'cards';
  onPresentationChange: (next: 'table' | 'cards') => void;
};

/**
 * The bar above the body: the bulk bar when rows are selected, otherwise the
 * filter bar. They are mutually exclusive in use, so rendering both stacks
 * two rows of controls above the data.
 */
function TableControls<TData extends DataTableRow>({
  resolved,
  query,
  presentation,
  onPresentationChange,
}: TableControlsProps<TData>) {
  const { tableState, toolbar, filterableColumns, columnFilters, setColumnFilter } = query;
  const selectedCount = tableState.selectedRowKeys.length;

  if (selectedCount > 0) {
    return (
      <BulkBar
        selectedCount={selectedCount}
        onClearSelection={() => tableState.table.resetRowSelection()}
      >
        {resolved.bulkActions}
      </BulkBar>
    );
  }
  if (!resolved.toolbar) return null;
  return (
    <FilterBar
      table={tableState.table}
      searchable={resolved.onSearchChange !== undefined}
      searchText={toolbar.searchText}
      onSearchInput={toolbar.onSearchInput}
      filterableColumns={filterableColumns}
      columnFilters={columnFilters}
      setColumnFilter={setColumnFilter}
      hideableColumnIds={toolbar.hideableColumnIds}
      toggleColumn={toolbar.toggleColumn}
      presentation={presentation}
      onPresentationToggle={() => onPresentationChange(presentation === 'table' ? 'cards' : 'table')}
      total={resolved.total}
    />
  );
}
